/**
 * The mount lifecycle of a clip-owning driver, AnimationPlayer or GLB. Each
 * Component keeps its clip construction, mixer root, pose snapshot and
 * `usePlaybackLoop` call. AnimationTree owns no clips, so it does not use this.
 */
import { useEffect, useRef } from 'react';
import { AnimationMixer } from 'three';
import { useAnimationTransport } from '../contexts/AnimationTransportContext';
import { useRegisterDriver } from '../contexts/AnimationDriverContext';
import { useRebindKey } from './useRebindKey';
export function useAnimationDriverMount(params) {
    const { object, clips, bind, nodePath, isActive, autoplay, durations, onMixerBuilt, restore } = params;
    const buildMixer = params.buildMixer ?? isActive;
    const transport = useAnimationTransport();
    const registerDriver = useRegisterDriver();
    const mixerRef = useRef(null);
    const actionsRef = useRef(new Map());
    /** Written only by the mixer build below: the binding it plays, until teardown. */
    const boundRef = useRef(null);
    const rebindKey = useRebindKey(boundRef);
    // Registered while selected, even with zero clips, so the Animation tab shows
    // "no animations" for an instanced driver outside the parse-time flattenedNodes.
    const { registerPlayer } = transport;
    useEffect(() => {
        if (!isActive)
            return;
        return registerPlayer({ clips: clips.map((c) => c.name), durations, autoplay });
    }, [isActive, clips, durations, autoplay, registerPlayer]);
    // Published whenever clips are loaded, selected or not, so an AnimationTree
    // whose `anim_player` resolves here can root its blended mixer.
    useEffect(() => {
        if (!object || clips.length === 0 || nodePath === null)
            return;
        return registerDriver(nodePath, { object, clips, bind });
    }, [object, clips, bind, nodePath, registerDriver]);
    // Built only while the driver would use one and is loaded (ADR-0012), and again when a load
    // mounts a target the binding missed. The
    // teardown stops, then restores, so the driver's snapshot wins over THREE's
    // own binding restore.
    useEffect(() => {
        if (!buildMixer || !object || clips.length === 0)
            return;
        const mixer = new AnimationMixer(object);
        const bound = bind();
        boundRef.current = bound;
        const actions = new Map();
        for (const clip of bound.clips) {
            actions.set(clip.name, mixer.clipAction(clip));
        }
        mixerRef.current = mixer;
        actionsRef.current = actions;
        onMixerBuilt(bound.targets);
        return () => {
            mixer.stopAllAction();
            restore();
            mixerRef.current = null;
            actionsRef.current = new Map();
            boundRef.current = null;
        };
    }, [buildMixer, object, clips, bind, onMixerBuilt, restore, rebindKey]);
    return { mixerRef, actionsRef };
}
//# sourceMappingURL=useAnimationDriverMount.js.map